import type {
  ProductsListItem,
  ProductsListResponse,
  ProductsSortField,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface FetchInventoryParams {
  page: number;               // 1-based (API convention)
  pageSize: number;
  sort?: ProductsSortField;
  order?: 'asc' | 'desc';
  search?: string;
  category?: string;
  includeInactive?: boolean;
  signal?: AbortSignal;
}

export async function fetchInventory(
  params: FetchInventoryParams,
): Promise<ProductsListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.search) qs.set('search', params.search);
  if (params.category) qs.set('category', params.category);
  if (params.includeInactive) qs.set('includeInactive', 'true');

  const response = await fetch(`${API_BASE_URL}/products?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch inventory: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
}

/** List products flagged as resale-license SKUs (cr001_is_license_key). */
export async function fetchLicenseProducts(signal?: AbortSignal): Promise<ProductsListItem[]> {
  const response = await fetch(`${API_BASE_URL}/products?licenseKeyOnly=true`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch license products: ${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  return data.items ?? [];
}

/** Set/clear the SM4RT-only is_license_key flag on a product (admin). */
export async function setLicenseKeyFlag(
  productId: string,
  isLicenseKey: boolean,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/products/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ isLicenseKey }),
    signal,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(
      `Failed to update product (${response.status} ${response.statusText}): ${detail.slice(0, 200)}`,
    );
  }
}
